import { type ComponentProps, type FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'reflexbox';
import styled from 'styled-components';

import { useViewer } from '@treams/treams-hooks';
import { Maybe, User, useViewerWithManagersQuery } from '@treams/treams-network';
import {
	AddIcon,
	Button,
	Callout,
	FlexGap,
	GraphQLErrorMessage,
	Popover,
	Separator,
	Subheading,
	UserSelect,
	tokens,
} from '@treams/treams-ui';
import { ShareUser } from '@treams/treams-ui/src/common/sharing/ShareUser';

type Props = {
	collaborators: User[];
	owner: User;
	subject?: Maybe<User> | undefined;
	shareMutation: (user: Pick<User, 'id'>) => Promise<unknown>;
	unshareMutation?: (user: Pick<User, 'id'>) => Promise<unknown>;
	mutationError?: ComponentProps<typeof GraphQLErrorMessage>['error'];
	subjectSubtitle?: string;
	ownerSubtitle?: string;
	contextWidth?: number;
	canShare?: boolean;
	entityType: 'conversation' | 'note' | 'oneOnOne';
	suggestManagers?: boolean;
};

const Footer = styled(Box)`
	background-color: ${tokens.surface.secondary};
`;

export const ShareModal: FC<Props> = ({
	collaborators,
	owner: incomingOwner,
	ownerSubtitle: incomingOwnerSubtitle,
	subject,
	subjectSubtitle: incomingSubjectSubtitle,
	shareMutation,
	unshareMutation,
	mutationError,
	contextWidth,
	canShare = true,
	entityType,
	suggestManagers,
}) => {
	const { t } = useTranslation();
	const viewer = useViewer();

	const { data: managersData } = useViewerWithManagersQuery({ skip: !suggestManagers });
	const managerGroup = useMemo(() => {
		const managerUserIDs = new Set(
			managersData?.viewer.managers.map((manager) => manager.source.id) ?? [],
		);
		return {
			title: t('sharing.modal.content.yourManagers'),
			userIDs: managerUserIDs,
		};
	}, [managersData, t]);

	const owner = collaborators.find((collaborator) => collaborator.id === incomingOwner.id);
	const isSharedWithSubject =
		subject && collaborators.some((collaborator) => collaborator.id === subject.id);

	const otherCollaborators = collaborators.filter(
		(collaborator) => collaborator.id !== owner?.id && collaborator.id !== subject?.id,
	);

	const ownerSubtitle = incomingOwnerSubtitle ?? t('sharing.modal.content.owner.subtitle');
	const subjectSubtitle = incomingSubjectSubtitle ?? t('sharing.modal.content.subject.subtitle');

	const isOwnerSubject = owner?.id === subject?.id;
	const showSubject = subject && !isOwnerSubject;

	return (
		<>
			<Box px={3}>
				<Box maxWidth={contextWidth ? contextWidth - 2 * 16 : undefined}>
					<GraphQLErrorMessage error={mutationError} />
					{!canShare && (
						<Callout
							mt={3}
							mb={2}
							color="yellow"
							description={t(`sharing.modal.content.cannotShare.${entityType}`)}
						/>
					)}
				</Box>
				<FlexGap flexDirection="column" py={12} gap={12}>
					{owner && (
						<ShareUser
							avatar={{
								user: owner,
							}}
							subtitle={
								isOwnerSubject ? [ownerSubtitle, subjectSubtitle].join(', ') : ownerSubtitle
							}
							viewerIsUser={owner.id === viewer?.id}
							onShare={() => {}}
							onStopSharing={() => {}}
						/>
					)}
					{owner && showSubject && <Separator />}
					{showSubject && (
						<ShareUser
							avatar={{
								user: subject,
							}}
							subtitle={subjectSubtitle}
							action={canShare ? (isSharedWithSubject ? 'stopSharing' : 'share') : undefined}
							onShare={() => shareMutation({ id: subject.id })}
							onStopSharing={() => unshareMutation?.({ id: subject.id })}
						/>
					)}
				</FlexGap>
			</Box>
			<Box>
				{otherCollaborators.length > 0 && (
					<>
						<Subheading mt={3} px={3}>
							{t('sharing.modal.content.share.with.others.title')}
						</Subheading>
						<FlexGap px={3} pt={3} py={12} gap={12} flexDirection="column">
							{otherCollaborators.map((collaborator) => (
								<ShareUser
									key={collaborator.id}
									avatar={{
										user: {
											firstName: collaborator.firstName,
											lastName: collaborator.lastName,
											avatar: collaborator.avatar
												? { source: { url: collaborator.avatar.source.url } }
												: undefined,
										},
									}}
									subtitle={t('sharing.modal.content.only.view.subtitle')}
									action={unshareMutation && canShare ? 'stopSharing' : undefined}
									onShare={() => {}}
									onStopSharing={() => unshareMutation?.(collaborator)}
								/>
							))}
						</FlexGap>
					</>
				)}
				{canShare && (
					<Footer width="100%" p={3} overflow="visible">
						<UserSelectRow
							excluded={collaborators.map((collaborator) => collaborator.id)}
							setSelected={(userID) => shareMutation({ id: userID })}
							suggestGroups={suggestManagers ? [managerGroup] : undefined}
						/>
					</Footer>
				)}
			</Box>
		</>
	);
};

function UserSelectRow(props: ComponentProps<typeof UserSelect.Menu>) {
	const { t } = useTranslation();
	const [visible, setVisible] = useState(false);

	return (
		<Popover.Target
			visible={visible}
			contents={
				<UserSelect.Menu
					{...props}
					setSelected={(...args) => {
						setVisible(false);
						props.setSelected(...args);
					}}
				/>
			}
			onDismiss={() => setVisible(false)}
			autofocus={true}
		>
			<Button
				type="secondary"
				icon={<AddIcon />}
				title={t('users.collaborators.selector.add.buttonTitle')}
				onClick={() => setVisible(true)}
			/>
		</Popover.Target>
	);
}
